/**
 * BKFC CMS Service
 *
 * Service for fetching events, fighters, fights, and news data
 * from the BKFC CMS API (mirrors BKFCContentService from the mobile app).
 *
 * Features:
 * - In-memory caching with configurable TTL
 * - Deduplication of concurrent in-flight requests
 * - Pagination helpers for events, fighters, and news
 * - Card-data builder for event detail pages
 * - Date / timezone utilities (via Intl)
 */

import { decode } from 'html-entities';
import { CMS_DATA_API_BASE_URL } from '../config/backendEndpoints';
import spotlightService from './SpotlightService';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const CMS_BASE_URL = CMS_DATA_API_BASE_URL;

const DEFAULT_LIMIT = 100;
const DEFAULT_OFFSET = 0;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const FIGHT_TYPE_IDS = {
  HEADLINE: '5c7e5d62e05ee02e084aa7256744e0e3',
  CO_HEADLINE: '4481c00e00fe2fc23ac64610228bd34e',
  FEATURED: '45d2aa7645e794f5ca700e17cdfd907b',
  UNDERCARD: '3cdf02ea9a3f8fcd3e553762b839cd0c',
};

export const NEWS_CONTENT_TYPE_IDS = {
  PRESS_RELEASE_LEGACY: '1b5616757916acdf25c1f8c3c984e792',
  PRESS_RELEASE: 'cb9da39579b1c29b55683f15d313b233',
  NEWS_LEGACY: '1736765918787-017624025686436173',
  NEWS: 'c6e75ba9a3da1a758a2994e0d1b0848f',
};

// ---------------------------------------------------------------------------
// BKFCContentService
// ---------------------------------------------------------------------------

class BKFCContentService {
  constructor() {
    this.baseURL = CMS_BASE_URL;

    // In-memory caches
    this.eventsCache = null;
    this.fightersCache = null;
    this.fightsCache = null;
    this.newsCache = null;
    this.gigcasterEventCache = {};
    this.slimEventCache = {};

    // Dedup concurrent requests for the same gigcaster ID
    this.inFlightGigcasterRequests = {};

    // Cache gigcaster IDs resolved from DotStudio channel slug lookups.
    // The underlying by-slug response is already cached in SpotlightService.
    this.channelGigcasterIdsCache = {};
    this.inFlightChannelGigcasterRequests = {};
  }

  // ==========================================================================
  // HTTP helpers
  // ==========================================================================

  async _fetch(path, options = {}) {
    const url = `${this.baseURL}${path}`;
    console.log(`[CMSService] Request: ${(options.method || 'GET').toUpperCase()} ${url}`);

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.error(`[CMSService] API Error ${response.status}: ${text}`);
      throw new Error(`CMS API error ${response.status}: ${text}`);
    }

    const data = await response.json();
    console.log(`[CMSService] Response: ${response.status} from ${path}`);
    return data;
  }

  // ==========================================================================
  // Date utilities (pure Intl / Date — no moment-timezone dependency)
  // ==========================================================================

  /**
   * Parse a CMS date string as UTC.
   *
   * CMS timestamps are timezone-naive ("YYYY-MM-DD HH:MM:SS", no offset) but
   * represent UTC. `new Date(str)` parses that space-separated form as LOCAL
   * time (V8), silently dropping the offset and shifting the wall-clock by the
   * viewer's TZ. Normalize to an explicit UTC instant so display, sorting, and
   * future/past checks all agree (and match channelDateUtils' `moment.utc`).
   *
   * @param {string} dateStr
   * @returns {Date} (Invalid Date if unparseable)
   */
  parseCmsDate(dateStr) {
    if (!dateStr) return new Date(NaN);
    const raw = String(dateStr).trim().replace(/ UTC$/i, '');
    // Already has an explicit zone/offset → trust it; otherwise treat as UTC.
    const norm = /[zZ]|[+-]\d\d:?\d\d$/.test(raw) ? raw : `${raw.replace(' ', 'T')}Z`;
    return new Date(norm);
  }

  /**
   * Format a CMS date string for display in the user's local timezone.
   *
   * @param {string} dateStr - ISO-ish date string (UTC)
   * @param {object} [opts] - Intl.DateTimeFormat options override
   * @returns {string} Formatted date string
   */
  formatDate(
    dateStr,
    opts = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    },
  ) {
    try {
      const date = this.parseCmsDate(dateStr);
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      return new Intl.DateTimeFormat('en-US', opts).format(date);
    } catch {
      return dateStr;
    }
  }

  /**
   * Short date format (e.g. "May 7, 2026")
   */
  formatDateShort(dateStr) {
    return this.formatDate(dateStr, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Check whether a date string is in the future.
   */
  isDateInFuture(dateStr) {
    try {
      return this.parseCmsDate(dateStr).getTime() > Date.now();
    } catch {
      return false;
    }
  }

  // ==========================================================================
  // Fight-type helpers
  // ==========================================================================

  getFightTypeTitle(fightTypeId) {
    switch (fightTypeId) {
      case FIGHT_TYPE_IDS.HEADLINE:
        return 'Headline Event';
      case FIGHT_TYPE_IDS.CO_HEADLINE:
        return 'Co Headline Event';
      case FIGHT_TYPE_IDS.FEATURED:
        return 'Featured Fight';
      case FIGHT_TYPE_IDS.UNDERCARD:
        return '';
      default:
        return '';
    }
  }

  _mapNewsContentType(contentType) {
    switch (contentType) {
      case NEWS_CONTENT_TYPE_IDS.PRESS_RELEASE_LEGACY:
      case NEWS_CONTENT_TYPE_IDS.PRESS_RELEASE:
        return 'Press Release';
      case NEWS_CONTENT_TYPE_IDS.NEWS_LEGACY:
      case NEWS_CONTENT_TYPE_IDS.NEWS:
        return 'News';
      default:
        return contentType || '--';
    }
  }

  // ==========================================================================
  // Events API
  // ==========================================================================

  /**
   * Fetch a page of events from the CMS.
   */
  async getEventsPaged({ limit = 10, offset = 0, searchFilter, timeframe } = {}) {
    const safeLimit = Math.min(Math.max(limit, 1), 100);
    const safeOffset = Math.max(offset, 0);

    const params = new URLSearchParams({
      limit: safeLimit.toString(),
      offset: safeOffset.toString(),
    });
    if (searchFilter) {
      params.set('search_filter', searchFilter);
    }
    if (timeframe) {
      params.set('timeframe', timeframe);
    }

    try {
      const data = await this._fetch(`/events/?${params}`);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('[CMSService] Failed to fetch events page:', error);
      throw error;
    }
  }

  /**
   * Get all events (auto-paginates). Results are cached in memory.
   */
  async getEvents(refresh = false) {
    if (this.eventsCache && !refresh) {
      return this.eventsCache;
    }

    try {
      const pageSize = 100;
      let offset = 0;
      let allEvents = [];
      let hasMore = true;

      while (hasMore) {
        const page = await this.getEventsPaged({ limit: pageSize, offset });
        allEvents = allEvents.concat(page);
        if (page.length < pageSize) {
          hasMore = false;
        } else {
          offset += pageSize;
        }
      }

      this.eventsCache = allEvents;
      return allEvents;
    } catch (error) {
      console.error('[CMSService] Failed to fetch all events:', error);
      throw error;
    }
  }

  /**
   * Find a single event by its CMS ID (uses the cached list).
   */
  async getEvent(eventId) {
    try {
      let events = await this.getEvents();
      let event = events.find(e => e.id === eventId);

      if (!event) {
        const refreshed = await this.getEvents(true);
        event = refreshed.find(e => e.id === eventId);
      }
      return event || null;
    } catch (error) {
      console.error('[CMSService] Failed to fetch event:', error);
      return null;
    }
  }

  /**
   * Fetch a single event by CMS ID with full fight + fighter data.
   */
  async getEventById(eventId) {
    if (!eventId) {
      return null;
    }

    const cacheKey = `event_by_id:${eventId}`;
    if (this.gigcasterEventCache[cacheKey]) {
      return this.gigcasterEventCache[cacheKey];
    }

    try {
      const data = await this._fetch(`/events/${eventId}`);
      if (data && data.id) {
        this.gigcasterEventCache[cacheKey] = data;
        return data;
      }
      return null;
    } catch (error) {
      console.error(`[CMSService] Failed to fetch event by ID ${eventId}:`, error);
      return null;
    }
  }

  /**
   * Get a single fight with full fighter + event data.
   */
  async getFightWithFighters(fightId) {
    if (!fightId) {
      return null;
    }

    try {
      const data = await this._fetch(`/fights/${fightId}`);
      return data && data.id ? data : null;
    } catch (error) {
      console.error(`[CMSService] Failed to fetch fight ${fightId}:`, error);
      return null;
    }
  }

  /**
   * Get event by Gigcaster ID. Results are cached; concurrent calls for the
   * same ID share one in-flight request.
   */
  async getEventByGigcasterId(gigcasterId) {
    if (!gigcasterId) {
      return null;
    }

    if (this.gigcasterEventCache[gigcasterId]) {
      return this.gigcasterEventCache[gigcasterId];
    }

    if (gigcasterId in this.inFlightGigcasterRequests) {
      return this.inFlightGigcasterRequests[gigcasterId];
    }

    const promise = (async () => {
      try {
        const data = await this._fetch(
          `/events/by-gigcaster?gigcaster_id=${encodeURIComponent(gigcasterId)}`,
        );
        if (data && Object.keys(data).length > 0) {
          this.gigcasterEventCache[gigcasterId] = data;
          return data;
        }
        return null;
      } catch (error) {
        console.error(`[CMSService] Failed to fetch event for gigcaster ID ${gigcasterId}:`, error);
        return null;
      } finally {
        delete this.inFlightGigcasterRequests[gigcasterId];
      }
    })();

    this.inFlightGigcasterRequests[gigcasterId] = promise;
    return promise;
  }

  /**
   * Get upcoming events (soonest first).
   */
  async getUpcomingEvents() {
    const events = await this.getEvents();
    return events
      .filter(e => this.isDateInFuture(e.event_date))
      .sort((a, b) => this.parseCmsDate(a.event_date) - this.parseCmsDate(b.event_date));
  }

  async getUpcomingEventsPaged(limit = 20, offset = 0) {
    return this.getEventsPaged({ limit, offset, timeframe: 'upcoming' });
  }

  /**
   * Get past events (most recent first).
   */
  async getPastEvents() {
    const events = await this.getEvents();
    return events
      .filter(e => !this.isDateInFuture(e.event_date))
      .sort((a, b) => this.parseCmsDate(b.event_date) - this.parseCmsDate(a.event_date));
  }

  async getPastEventsPaged(limit = 20, offset = 0) {
    return this.getEventsPaged({ limit, offset, timeframe: 'past' });
  }

  /**
   * Preload event data for an array of gigcaster IDs.
   */
  async preloadEventData(gigcasterIds) {
    if (!gigcasterIds || gigcasterIds.length === 0) {
      return;
    }

    const promises = gigcasterIds
      .filter(id => id && !this.gigcasterEventCache[id])
      .map(id => this.getEventByGigcasterId(id));

    await Promise.all(promises);
  }

  // ==========================================================================
  // Two-tier loading (slim for homepage, full for detail pages)
  // ==========================================================================

  /**
   * Bulk-fetch slim event data for homepage display.
   *
   * Uses GET /events/by-gigcaster-bulk?ids=<sorted,comma-separated> so the
   * response is edge-cacheable (a POST never is). Two rules make the Cloudflare
   * cache effective:
   *   1. ids are de-duped and SORTED, so every client requesting the same set
   *      produces the identical URL -> one shared edge cache entry.
   *   2. the sorted set is split into URL-length-bounded chunks so we never
   *      exceed browser/CDN URL limits; the chunking is deterministic (greedy
   *      over the sorted list), so chunk URLs are shared across clients too.
   *
   * We deliberately request the FULL sorted set rather than a per-client
   * "uncached" subset: a varying subset would fragment the URL and wreck the hit
   * rate. We still skip the network entirely when everything is already cached.
   */
  /**
   * Fetch LIVE (never-cached) event status for a single gigcaster id.
   *
   * Returns the volatile ops-controlled fields that decide the countdown vs
   * WATCH NOW experience: { event_id, event_date, start_time, end_time,
   * hide_countdown }. Deliberately bypasses slimEventCache/gigcasterEventCache —
   * a last-minute reschedule, a hidden countdown, or a corrected bad date must
   * take effect immediately. Returns null when the gigcaster id is unknown.
   */
  async getLiveStatus(gigcasterId) {
    if (!gigcasterId) {
      return null;
    }
    const data = await this._fetch(
      `/events/live-status?gigcaster_id=${encodeURIComponent(gigcasterId)}`,
    );
    return data && Object.keys(data).length > 0 ? data : null;
  }

  async preloadSlimEventData(gigcasterIds) {
    if (!gigcasterIds || gigcasterIds.length === 0) {
      return;
    }

    // De-dupe + sort -> deterministic, shareable URLs across clients.
    const ids = [...new Set(gigcasterIds.filter(Boolean))].sort();
    if (ids.length === 0 || ids.every(id => this.slimEventCache[id])) {
      return;
    }

    const PATH = '/events/by-gigcaster-bulk?ids=';
    try {
      const responses = await Promise.all(
        this._chunkIdsForUrl(ids, PATH.length).map(chunk =>
          this._fetch(`${PATH}${chunk.join(',')}`).catch(error => {
            console.error('[CMSService] slim event chunk failed:', error);
            return {};
          }),
        ),
      );

      responses.forEach(data => {
        Object.entries(data || {}).forEach(([id, event]) => {
          this.slimEventCache[id] = event;
        });
      });
    } catch (error) {
      console.error('[CMSService] Failed to bulk fetch slim events:', error);
    }
  }

  /**
   * Split a SORTED id list into chunks whose FULL request path (prefixLen +
   * encoded `ids=` value) stays under a deliberately conservative budget.
   *
   * Budget = 1800 chars for the whole `path?query`. The limit chain: browsers
   * allow tens of KB, Cloudflare ~32 KB, and the binding limit -- the nginx/Kong
   * request line -- is ~8 KB (the host travels in the Host header, not the
   * request line). 1800 leaves >4x headroom under 8 KB and stays under even the
   * legacy 2 KB ceiling, so no proxy in the chain can 414 us.
   *
   * Greedy over the SORTED input so boundaries are deterministic -> chunk URLs
   * are shared across clients (good Cloudflare hit rate). Each id is
   * encodeURIComponent'd, so the measured length is the real on-the-wire length.
   * Returns arrays of already-encoded ids.
   */
  _chunkIdsForUrl(ids, prefixLen = 0) {
    const MAX_PATH_LEN = 1800;
    const budget = Math.max(1, MAX_PATH_LEN - prefixLen);
    const chunks = [];
    let current = [];
    let length = 0;

    for (const id of ids) {
      const encoded = encodeURIComponent(id);
      // +1 for the joining comma, but only when this isn't the first id in the chunk.
      const addition = encoded.length + (length === 0 ? 0 : 1);
      if (length > 0 && length + addition > budget) {
        chunks.push(current);
        current = [];
        length = 0;
      }
      current.push(encoded);
      length += length === 0 ? encoded.length : addition;
    }

    if (current.length) {
      chunks.push(current);
    }
    return chunks;
  }

  getSlimEventData(gigcasterId) {
    return this.slimEventCache[gigcasterId] || null;
  }

  hasSlimEventData(gigcasterId) {
    return gigcasterId in this.slimEventCache;
  }

  /**
   * Synchronously get the first cached gigcaster ID for a channel slug.
   * Returns null if not yet resolved. Call resolveGigcasterIdsForChannel first.
   */
  getCachedGigcasterIdForSlug(slug) {
    if (!slug) return null;
    const ids = this.channelGigcasterIdsCache[slug];
    return ids?.[0] || null;
  }

  /**
   * Get full event data (for detail/PDP pages).
   */
  async getFullEventData(gigcasterId) {
    if (!gigcasterId) {
      return null;
    }

    if (this.gigcasterEventCache[gigcasterId]) {
      return this.gigcasterEventCache[gigcasterId];
    }
    return this.getEventByGigcasterId(gigcasterId);
  }

  hasFullEventData(gigcasterId) {
    return gigcasterId in this.gigcasterEventCache;
  }

  /**
   * Resolve gigcaster IDs for a DotStudio channel object.
   *
   * Strategy:
   * 1. Extract IDs directly from the provided channel object.
   * 2. If none exist, lazily fetch a detailed channel by slug (cached by SpotlightService).
   * 3. Cache resolved IDs by slug for future lookups.
   */
  async resolveGigcasterIdsForChannel(channel) {
    const directIds = extractGigcasterIdsFromChannel(channel);
    if (directIds.length > 0) {
      return directIds;
    }

    const slug = channel?.slug || channel?.strSlug;
    if (!slug) {
      return [];
    }

    if (Object.prototype.hasOwnProperty.call(this.channelGigcasterIdsCache, slug)) {
      return this.channelGigcasterIdsCache[slug];
    }

    if (slug in this.inFlightChannelGigcasterRequests) {
      return this.inFlightChannelGigcasterRequests[slug];
    }

    const promise = (async () => {
      try {
        const response = await spotlightService.getChannelBySlug(slug);
        const detailedChannel =
          response?.channels?.[0] || response?.channel || response?.data?.channels?.[0] || null;

        const ids = extractGigcasterIdsFromChannel(detailedChannel);
        this.channelGigcasterIdsCache[slug] = ids;

        return ids;
      } catch (error) {
        console.error(`[CMSService] Failed bySlug gigcaster lookup for "${slug}":`, error);
        this.channelGigcasterIdsCache[slug] = [];
        return [];
      } finally {
        delete this.inFlightChannelGigcasterRequests[slug];
      }
    })();

    this.inFlightChannelGigcasterRequests[slug] = promise;
    return promise;
  }

  // ==========================================================================
  // Fighters API
  // ==========================================================================

  /**
   * Get all fighters.
   */
  async getFighters(refresh = false) {
    if (this.fightersCache && !refresh) {
      return this.fightersCache;
    }

    try {
      const data = await this._fetch('/fighters/');
      this.fightersCache = data;
      return data;
    } catch (error) {
      console.error('[CMSService] Failed to fetch fighters:', error);
      throw error;
    }
  }

  /**
   * Get a single fighter by ID (returns formatted listing object for backward compat).
   */
  async getFighter(fighterId) {
    try {
      const fighter = await this._fetch(`/fighters/${fighterId}`);
      return {
        id: fighter.id,
        title: decode(fighter.name),
        ranking: '--',
        img: fighter.avatar_image_url || '',
        wins: fighter.wins || 0,
        losses: fighter.losses || 0,
        draws: fighter.draws || 0,
        champion: fighter.champion !== ' ' && !!fighter.champion,
        fullFighter: fighter,
      };
    } catch (error) {
      console.error('[CMSService] Failed to fetch fighter:', error);
      return null;
    }
  }

  /**
   * Get a single fighter's raw data by ID (no mapping).
   */
  async getFighterRaw(fighterId) {
    try {
      return await this._fetch(`/fighters/${fighterId}`);
    } catch (error) {
      console.error('[CMSService] Failed to fetch fighter raw:', error);
      return null;
    }
  }

  /**
   * Get fighters with pagination and filters.
   */
  async getFightersPaged({
    offset = DEFAULT_OFFSET,
    limit = DEFAULT_LIMIT,
    weight_class = '',
    initial = '',
    name_like = '',
    order_by = '',
  } = {}) {
    try {
      const params = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
        initial: initial.toLowerCase(),
        weight_class,
        name_like,
        order_by,
      });

      const data = await this._fetch(`/fighters?${params}`);
      return data.map(fighter => ({
        id: fighter.id,
        title: decode(fighter.name),
        ranking: '--',
        img: fighter.avatar_image_url || '',
        wins: fighter.wins || 0,
        losses: fighter.losses || 0,
        draws: fighter.draws || 0,
        champion: fighter.champion !== ' ' && !!fighter.champion,
        fullFighter: fighter,
      }));
    } catch (error) {
      console.error('[CMSService] Failed to fetch fighters paged:', error);
      return [];
    }
  }

  /**
   * Get a fighter's fight record / history.
   */
  async getFighterRecord(fighterId) {
    try {
      const data = await this._fetch(`/fighter_record/${fighterId}`);
      if (!data || data.length === 0) {
        return [];
      }

      return data.map(fight => ({
        ...fight,
        event: {
          ...fight.event,
          event_date: this.formatDateShort(fight.event.event_date),
        },
      }));
    } catch (error) {
      console.error('[CMSService] Failed to fetch fighter record:', error);
      return [];
    }
  }

  /**
   * Get a fighter's media (videos, photos).
   */
  async getFighterMedia(fighterId) {
    try {
      const data = await this._fetch(`/fighter_media/${fighterId}`);
      if (!data || Object.keys(data).length === 0) {
        return [];
      }
      return data;
    } catch (error) {
      console.error('[CMSService] Failed to fetch fighter media:', error);
      return [];
    }
  }

  /**
   * Search fighters by name.
   */
  async searchFighters(searchTerm) {
    return this.getFightersPaged({ name_like: searchTerm });
  }

  // ==========================================================================
  // Fights API
  // ==========================================================================

  /**
   * Get all fights.
   */
  async getFights(refresh = false) {
    if (this.fightsCache && !refresh) {
      return this.fightsCache;
    }

    try {
      const data = await this._fetch('/fights/');
      this.fightsCache = data;
      return data;
    } catch (error) {
      console.error('[CMSService] Failed to fetch fights:', error);
      throw error;
    }
  }

  /**
   * Get a single fight by ID.
   */
  async getFight(fightId) {
    try {
      return await this._fetch(`/fights/${fightId}`);
    } catch (error) {
      console.error('[CMSService] Failed to fetch fight:', error);
      return null;
    }
  }

  // ==========================================================================
  // News API
  // ==========================================================================

  /**
   * Get all news articles.
   */
  async getNewsAll(refresh = false) {
    if (this.newsCache && !refresh) {
      return this.newsCache;
    }

    try {
      const data = await this._fetch('/news/');
      this.newsCache = data;
      return data;
    } catch (error) {
      console.error('[CMSService] Failed to fetch news:', error);
      throw error;
    }
  }

  /**
   * Get a single news article by ID.
   */
  async getNews(newsId) {
    try {
      return await this._fetch(`/news/${newsId}`);
    } catch (error) {
      console.error('[CMSService] Failed to fetch news article:', error);
      return null;
    }
  }

  /**
   * Get news articles with pagination and filters.
   */
  async getNewsPaged({
    offset = DEFAULT_OFFSET,
    limit = DEFAULT_LIMIT,
    type = '',
    search_filter = '',
  } = {}) {
    try {
      const params = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
        type,
        search_filter,
      });

      const data = await this._fetch(`/news?${params}`);

      const validEntries = data.filter(item => {
        if (!item.title || typeof item.title !== 'string' || item.title.trim() === '') {
          return false;
        }
        if (!item.date_of_publish || typeof item.date_of_publish !== 'string') {
          return false;
        }
        return true;
      });

      return validEntries.map((item, index) => ({
        id: index + offset,
        title: item.title,
        type: this._mapNewsContentType(item.content_type || ''),
        date: this.formatDateShort(item.date_of_publish),
        imageUrl: item.main_image_url,
        summary: item.seo_description,
        fullArticle: item,
      }));
    } catch (error) {
      console.error('[CMSService] Failed to fetch news paged:', error);
      return [];
    }
  }

  /**
   * Search news articles.
   */
  async searchNews(searchTerm) {
    return this.getNewsPaged({ search_filter: searchTerm });
  }

  // ==========================================================================
  // Card Data (combined Event + Fight data for display)
  // ==========================================================================

  /**
   * Build card data for an event's fights, given a gigcaster ID.
   */
  async getCardDataByGigcasterId(gigcasterId) {
    if (!gigcasterId) {
      return null;
    }

    const event = await this.getEventByGigcasterId(gigcasterId);
    if (!event) {
      return null;
    }

    return this._buildCardDataFromEvent(event);
  }

  /**
   * Build card data for an event's fights, given a CMS event ID.
   */
  async getCardDataByEventId(eventId) {
    const event = await this.getEvent(eventId);

    if (!event) {
      return null;
    }
    if (event.gigcaster_id) {
      return this.getCardDataByGigcasterId(event.gigcaster_id);
    }
    if (event.fights) {
      return this._buildCardDataFromEvent(event);
    }
    return null;
  }

  _buildCardDataFromEvent(event) {
    const cards = [];

    for (const fight of event.fights || []) {
      if (!fight) {
        continue;
      }

      const fighter1 = fight.fighter1;
      const fighter2 = fight.fighter2;
      if (!fighter1 || !fighter2) {
        continue;
      }

      cards.push({
        event: {
          title: event.name,
          date: this.formatDate(event.event_date),
          ts: this.parseCmsDate(event.event_date).getTime(),
          championship:
            fight.fight_type || this.getFightTypeTitle(fight.fight_type_import || '') || '',
          venue_name: event.venue_name,
          location:
            event.location ||
            event.city_state ||
            [event.city, event.state].filter(Boolean).join(', ') ||
            '',
          is_future: this.isDateInFuture(event.event_date),
          buy_tickets_url: event.buy_tickets_url,
          slug: event.slug,
        },
        fighters: {
          is_main_card: fight.fight_type !== 'Undercard',
          fighter1: {
            name: decode(fighter1.name),
            country: fighter1.country || '',
            flag: '',
            record: `${fighter1.wins || 0}-${fighter1.draws || 0}-${fighter1.losses || 0}`,
            weight: fighter1.weight ? `${fighter1.weight} LBS` : '-- LBS',
            odds: '--',
            image: fighter1.profile_image_url || null,
            fullFighter: fighter1,
          },
          fighter2: {
            name: decode(fighter2.name),
            country: fighter2.country || '',
            flag: '',
            record: `${fighter2.wins || 0}-${fighter2.draws || 0}-${fighter2.losses || 0}`,
            weight: fighter2.weight ? `${fighter2.weight} LBS` : '-- LBS',
            odds: '--',
            image: fighter2.profile_image_url || null,
            fullFighter: fighter2,
          },
        },
        order: fight.fight_order || 0,
      });
    }

    cards.sort((a, b) => a.order - b.order);
    return cards;
  }

  // ==========================================================================
  // Cache management
  // ==========================================================================

  clearCache() {
    this.eventsCache = null;
    this.fightersCache = null;
    this.fightsCache = null;
    this.newsCache = null;
    this.gigcasterEventCache = {};
    this.slimEventCache = {};
    this.channelGigcasterIdsCache = {};
    this.inFlightChannelGigcasterRequests = {};
    console.log('[CMSService] All caches cleared');
  }

  clearCacheFor(type) {
    switch (type) {
      case 'events':
        this.eventsCache = null;
        break;
      case 'fighters':
        this.fightersCache = null;
        break;
      case 'fights':
        this.fightsCache = null;
        break;
      case 'news':
        this.newsCache = null;
        break;
      case 'gigcaster':
        this.gigcasterEventCache = {};
        this.channelGigcasterIdsCache = {};
        this.inFlightChannelGigcasterRequests = {};
        break;
      case 'slim':
        this.slimEventCache = {};
        break;
      default:
        break;
    }
  }

  getCacheStats() {
    return {
      slimEventsCount: Object.keys(this.slimEventCache).length,
      fullEventsCount: Object.keys(this.gigcasterEventCache).length,
      eventsListCount: this.eventsCache?.length || 0,
    };
  }
}

// ---------------------------------------------------------------------------
// Singleton export + backward-compatible named exports
// ---------------------------------------------------------------------------

const cmsService = new BKFCContentService();

export default cmsService;
export { cmsService };

// Legacy named exports used by FighterDetailPage and FightersPage
export const getEventFromGigcasterId = gigcasterId => cmsService.getEventByGigcasterId(gigcasterId);

export const getFighter = id => cmsService.getFighter(id);

export const getFighterListingPaged = (
  offset = 0,
  weightClass = '',
  letter = '',
  searchText = '',
  orderBy = '',
) =>
  cmsService.getFightersPaged({
    offset,
    weight_class: weightClass,
    initial: letter,
    name_like: searchText,
    order_by: orderBy,
  });

// ---------------------------------------------------------------------------
// Gigcaster ID extraction (ported from mobile HomePagePreloadService)
// ---------------------------------------------------------------------------

function extractGigcasterIdFromCustomFields(customFields) {
  if (!customFields) {
    return null;
  }
  if (typeof customFields === 'object' && !Array.isArray(customFields)) {
    return customFields.gigcasters_show_id || customFields.gigcaster_id || null;
  }
  if (Array.isArray(customFields)) {
    const field = customFields.find(
      f => f.field_title === 'gigcasters_show_id' || f.field_title === 'gigcaster_id',
    );
    return field?.field_value || null;
  }
  return null;
}

function extractFromVideo(video) {
  if (!video) {
    return null;
  }
  return (
    video.mrss_guid ||
    video.video_gigcaster_id ||
    video.gigcaster_id ||
    extractGigcasterIdFromCustomFields(video.custom_fields)
  );
}

function addUnique(arr, val) {
  if (val && !arr.includes(val)) {
    arr.push(val);
  }
}

function addVideoListIds(ids, videos) {
  if (!videos?.length) {
    return;
  }
  videos.forEach(video => {
    addUnique(ids, extractFromVideo(video));
  });
}

/**
 * Extract all gigcaster IDs from a DotStudio channel object.
 * Mirrors the mobile app's extractGigcasterIdsFromChannel logic.
 *
 * @param {object} channel - DotStudio channel object
 * @returns {string[]} Array of gigcaster IDs found
 */
export function extractGigcasterIdsFromChannel(channel) {
  if (!channel) {
    return [];
  }

  const ids = [];
  addVideoListIds(ids, channel.spltVideos);
  addVideoListIds(ids, channel.playlist);
  addVideoListIds(ids, channel.contents);
  addVideoListIds(ids, channel.channels);
  addUnique(ids, channel._gigcasterId);
  addUnique(ids, channel.video_gigcaster_id);
  addUnique(ids, channel.gigcasterId);
  addUnique(ids, channel.gigcaster_id);
  addUnique(ids, extractFromVideo(channel.video));
  addUnique(ids, extractGigcasterIdFromCustomFields(channel.custom_fields));

  return ids;
}

/**
 * Extract the first gigcaster ID from a channel object.
 *
 * @param {object} channel - DotStudio channel object
 * @returns {string|null} First gigcaster ID or null
 */
export function extractGigcasterIdFromChannel(channel) {
  return extractGigcasterIdsFromChannel(channel)[0] || null;
}
